import { useCallback, useEffect, useRef } from "react";

interface UseMapZoomProps {
  map?: google.maps.Map;
  zoomSensitivity?: number;
}

export const useMapZoom = ({
  map,
  zoomSensitivity = 1,
}: UseMapZoomProps = {}) => {
  const wheelListener = useRef<((e: WheelEvent) => void) | null>(null);
  const isZooming = useRef(false);
  const zoomTimeout = useRef<NodeJS.Timeout>();
  const lastDelta = useRef(0);

  const applyZoom = useCallback(
    (deltaZoom: number) => {
      if (!map) return;
      const center = map.getCenter();
      if (!center) return;

      const currentZoom = map.getZoom() || 0;
      const targetZoom = Math.min(Math.max(currentZoom + deltaZoom, 1), 22);

      map.moveCamera({
        zoom: targetZoom,
        center,
      });
    },
    [map]
  );

  const startZooming = useCallback(
    (delta: number) => {
      if (zoomTimeout.current) {
        clearTimeout(zoomTimeout.current);
      }

      lastDelta.current = delta;
      isZooming.current = true;

      const doZoom = () => {
        if (isZooming.current) {
          applyZoom(lastDelta.current);
          requestAnimationFrame(doZoom);
        }
      };

      doZoom();

      zoomTimeout.current = setTimeout(() => {
        isZooming.current = false;
      }, 150);
    },
    [applyZoom]
  );

  useEffect(() => {
    if (!map) return;

    if (wheelListener.current) {
      const mapDiv = map.getDiv();
      mapDiv.removeEventListener("wheel", wheelListener.current);
    }

    wheelListener.current = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const normalFactor = 0.05;

      let zoomFactor;
      if (zoomSensitivity <= 0.3) {
        zoomFactor = normalFactor * 0.1;
      } else if (zoomSensitivity <= 0.6) {
        zoomFactor = normalFactor * 0.3;
      } else if (zoomSensitivity <= 0.8) {
        zoomFactor = normalFactor * 0.6;
      } else if (zoomSensitivity <= 1.2) {
        zoomFactor = normalFactor;
      } else if (zoomSensitivity <= 1.5) {
        zoomFactor = normalFactor * 2;
      } else {
        zoomFactor = normalFactor * 4;
      }

      const deltaZoom = Math.sign(e.deltaY) * -zoomFactor;

      if (isZooming.current) {
        lastDelta.current = deltaZoom;
      } else {
        startZooming(deltaZoom);
      }
    };

    const mapDiv = map.getDiv();
    mapDiv.addEventListener("wheel", wheelListener.current, { passive: false });

    return () => {
      if (map && wheelListener.current) {
        if (zoomTimeout.current) {
          clearTimeout(zoomTimeout.current);
        }
        isZooming.current = false;
        map.getDiv().removeEventListener("wheel", wheelListener.current);
      }
    };
  }, [map, zoomSensitivity, startZooming]);

  const setZoom = useCallback(
    (zoom: number) => {
      if (!map) return;
      const delta = zoom - (map.getZoom() || 0);
      applyZoom(delta);
    },
    [map, applyZoom]
  );

  const fitBounds = useCallback(
    (bounds: google.maps.LatLngBounds, padding?: number) => {
      if (!map) return;
      map.fitBounds(bounds, padding);
    },
    [map]
  );

  return {
    setZoom,
    fitBounds,
  };
};
